import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { supabase } from '../lib/supabase/client';

interface AuthContextType {
  user: any | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const getSession = async () => {
      try {
        console.log('Auth: Getting session...');
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('Auth: Error getting session:', error);
          setUser(null);
        } else if (session) {
          console.log('Auth: Session found:', session.user);
          setUser(session.user);
        } else {
          console.log('Auth: No session found');
          setUser(null);
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Auth: Exception in getSession:', err);
        setUser(null);
        setLoading(false);
      }
    };

    getSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('Auth: State changed:', event, session?.user);
      
      if (session?.user) {
        setUser(session.user);
      } else {
        setUser(null);
      }
      
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    console.log('Auth: Signing in...', email);
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    
    if (error) {
      console.error('Auth: Sign in error:', error);
      throw error;
    }
    
    console.log('Auth: Sign in successful:', data);
  };

  const signUp = async (email: string, password: string) => {
    console.log('Auth: Signing up...', email);
    const { data, error } = await supabase.auth.signUp({ email, password });
    
    if (error) {
      console.error('Auth: Sign up error:', error);
      throw error;
    }
    
    console.log('Auth: Sign up successful:', data);
  };

  const signOut = async () => {
    console.log('Auth: Signing out...');
    const { error } = await supabase.auth.signOut();
    
    if (error) {
      console.error('Auth: Sign out error:', error);
      throw error;
    }
    
    console.log('Auth: Sign out successful');
  };

  return (
    <AuthContext.Provider value={{ user, loading, signIn, signUp, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
